import React from 'react';
import {
  Banner,
  Button,
  Flex,
  Select,
  Switch,
  Text,
  TextArea,
  TextField,
  useToast,
  type SelectOption,
} from '@chatsdet/ui';
import { useAuth } from '../../../contexts/AuthContext';
import {
  useTicketEnvironments,
  useTicketProperties,
  useUpdateTicketSettings,
} from '../useTickets';
import {
  DEFAULT_TICKET_TAG,
  TICKET_INSTRUCTIONS_MAX_LENGTH,
  isSelectableTicketEnvironment,
  ticketEnvironmentLabel,
  type TicketSettings,
} from '../types';

const DEFAULT_ENV_KEY = '__property_default__';

/**
 * Form for tag-triggered ticket testing on Jira, the only provider that supports it.
 * The caller decides placement (inline card or dialog); `onSaved` runs after a successful save.
 */
export function TicketConfigPanel({
  settings,
  onSaved,
}: {
  settings: TicketSettings;
  onSaved?: () => void;
}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = user?.role === 'admin';
  const update = useUpdateTicketSettings();
  const propertiesQuery = useTicketProperties();
  const properties = propertiesQuery.data?.items ?? [];

  const [label, setLabel] = React.useState(settings.tagTriggerLabel ?? DEFAULT_TICKET_TAG);
  const [propertyId, setPropertyId] = React.useState<string | null>(settings.tagTriggerPropertyId);
  const [environmentId, setEnvironmentId] = React.useState<string | null>(
    settings.tagTriggerEnvironmentId
  );
  const [instructions, setInstructions] = React.useState(settings.tagTriggerInstructions ?? '');
  const [allowMapUpdates, setAllowMapUpdates] = React.useState(settings.ticketRunAllowMapUpdates);

  const environmentsQuery = useTicketEnvironments(propertyId);
  const environmentOptions: SelectOption[] = React.useMemo(() => {
    const items = (environmentsQuery.data?.items ?? []).filter(isSelectableTicketEnvironment);
    return [
      { id: DEFAULT_ENV_KEY, label: 'Application default' },
      ...items.map((environment) => ({
        id: environment.id,
        label: ticketEnvironmentLabel(environment),
        description: environment.url ?? undefined,
      })),
    ];
  }, [environmentsQuery.data]);

  const trimmedLabel = label.trim();
  const trimmedInstructions = instructions.trim();
  const instructionsTooLong = instructions.length > TICKET_INSTRUCTIONS_MAX_LENGTH;
  const mapUpdatesChanged = allowMapUpdates !== settings.ticketRunAllowMapUpdates;
  const isDirty =
    trimmedLabel !== (settings.tagTriggerLabel ?? '') ||
    propertyId !== settings.tagTriggerPropertyId ||
    environmentId !== settings.tagTriggerEnvironmentId ||
    trimmedInstructions !== (settings.tagTriggerInstructions ?? '') ||
    mapUpdatesChanged;

  const save = () => {
    update.mutate(
      {
        tagTriggerLabel: trimmedLabel,
        tagTriggerPropertyId: propertyId,
        tagTriggerEnvironmentId: environmentId,
        tagTriggerInstructions: trimmedInstructions.length > 0 ? trimmedInstructions : null,
        ...(isAdmin && mapUpdatesChanged ? { ticketRunAllowMapUpdates: allowMapUpdates } : {}),
      },
      {
        onSuccess: () => {
          toast.success('Ticket settings saved');
          onSaved?.();
        },
      }
    );
  };

  return (
    <Flex direction="column" gap="lg">
      {update.error && <Banner variant="error">{update.error.message}</Banner>}
      <TextField
        label="Tag"
        value={label}
        onChange={setLabel}
        placeholder={`e.g. ${DEFAULT_TICKET_TAG}`}
        description="Leave blank to turn off. Must match the Jira label exactly."
      />
      <Select
        label="Application"
        selectedKey={propertyId}
        items={properties.map((property) => ({ id: property.id, label: property.name }))}
        placeholder="Select an application..."
        onSelectionChange={(key) => {
          setPropertyId((key as string) ?? null);
          setEnvironmentId(null);
        }}
      />
      {propertyId && (
        <Select
          label="Environment"
          selectedKey={environmentId ?? DEFAULT_ENV_KEY}
          items={environmentOptions}
          onSelectionChange={(key) =>
            setEnvironmentId(key === DEFAULT_ENV_KEY ? null : (key as string))
          }
        />
      )}
      <TextArea
        label="Additional instructions"
        value={instructions}
        onChange={setInstructions}
        rows={4}
        description="Added after the ticket's title and description"
        errorMessage={
          instructionsTooLong
            ? `Keep this under ${TICKET_INSTRUCTIONS_MAX_LENGTH} characters`
            : undefined
        }
      />
      {isAdmin && (
        <div>
          <Switch
            isSelected={allowMapUpdates}
            onChange={setAllowMapUpdates}
            label="Let the agent add what it verifies to the product map"
          />
          <Text size="sm" className="text-muted-foreground mt-1">
            Off by default. Ticket text can steer what the agent writes, so only admins can
            change this.
          </Text>
        </div>
      )}
      <Flex gap="sm">
        <Button
          variant="primary"
          size="sm"
          onPress={save}
          isDisabled={!isDirty || instructionsTooLong || update.isPending}
        >
          {update.isPending ? 'Saving...' : 'Save'}
        </Button>
      </Flex>
    </Flex>
  );
}
